import type { ReactNode } from 'react';
import { ApiError, AuthRequiredError } from '../api/client';
import type { Readiness, WindowDays } from '../api/types';
import { loginPath } from '../lib/auth';
import { formatDay, isFirstPass } from '../lib/format';

/** Loading skeleton rows, matching the mockup's `skelRows`. */
export function SkeletonRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <tr key={r} data-testid="skeleton-row">
          {Array.from({ length: cols }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder cells
            <td key={i}>
              <div className="skel" style={{ width: i ? 50 + ((i * 37) % 40) : 140 }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function SkeletonBlock({ height = 12, width }: { height?: number; width?: string | number }) {
  return <div className="skel" style={{ height, width }} />;
}

/** Hatched "collecting data" panel (mockup `.collect`). */
export function CollectingPanel({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div className="collect" data-testid={testId ?? 'collecting'}>
      {children}
    </div>
  );
}

/**
 * Copy for a collecting window (approved mockups `mockups/collecting-copy.html` Variant A and
 * `mockups/first-pass-followups.html` items 2–3). `rows` names what's in the table below; pass it only when rows
 * are on screen.
 */
export function collectingText(
  window: WindowDays,
  readiness: Readiness,
  rows?: 'Values' | 'Scores',
): ReactNode {
  if (readiness.status === 'ready') return null;
  if (isFirstPass(readiness)) {
    return (
      <>
        <b>First pass of the catalog is still running.</b> Each night fetches about a quarter of the listed
        islands. Window days start counting once 95% have been fetched at least once (about a week after
        launch).
        {rows
          ? " The numbers below are real, but they don't cover every island yet, so they're provisional (dotted underline)."
          : ''}{' '}
        Nothing to do.
      </>
    );
  }
  const text = `Collecting data for the ${window}d window: ${readiness.have} of ${readiness.need} days stored. This window fills in by itself; nothing to do.`;
  return rows
    ? `${text} ${rows} below are provisional (dotted underline) until all ${readiness.need} days are stored.`
    : text;
}

/** "Couldn't reach the Studio API" banner; shows the last good snapshot date when data is still on screen. */
export function ApiErrorBanner({
  error,
  lastGoodAsOf,
  onRetry,
}: {
  error: Error | null;
  lastGoodAsOf?: string | null | undefined;
  onRetry?: () => void;
}) {
  if (!error) return null;
  if (error instanceof AuthRequiredError) {
    return (
      <div className="banner err" role="alert">
        <b>Your sign-in has ended.</b>
        <span>
          <a className="btn sm" href={loginPath(window.location.pathname + window.location.search)}>
            Sign in again
          </a>
        </span>
      </div>
    );
  }
  return (
    <div className="banner err" role="alert" data-testid="api-error">
      <b>Couldn't reach the Studio API.</b>
      <span>
        {lastGoodAsOf
          ? `Numbers below are from the last good snapshot (${formatDay(lastGoodAsOf)}). `
          : 'Nothing is cached for this view yet. '}
        Check that the API service is up on Railway, then retry.{' '}
        {onRetry ? (
          <button type="button" className="btn sm" onClick={onRetry}>
            Retry
          </button>
        ) : null}
        {error instanceof ApiError && error.status >= 400 ? (
          <span className="stamp"> ({error.message})</span>
        ) : null}
      </span>
    </div>
  );
}

/** Phase 4–5 endpoints answer 404 until the parallel backend work lands. */
export function NotAvailable({ what, detail }: { what: string; detail?: string }) {
  return (
    <div className="panel empty" data-testid="not-available">
      <span className="pill p-mute">Not available yet</span>
      <h2>{what} is not available yet</h2>
      <p className="muted" style={{ maxWidth: '52ch' }}>
        {detail ??
          'The API for this screen is still being built. This page will start working as soon as it is deployed; nothing to do here.'}
      </p>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="panel empty" data-testid="empty">
      <h2>{title}</h2>
      {children ? (
        <p className="muted" style={{ maxWidth: '52ch' }}>
          {children}
        </p>
      ) : null}
    </div>
  );
}

export function isNotAvailable(err: Error | null): boolean {
  return err instanceof ApiError && err.isNotAvailable;
}
